import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import "./Login.css";
import { API_BASED_URL } from '../config.js';
import {
  sanitizeInput,
  validateEmail,
  setAuthToken,
  createRateLimiter
} from '../utils/security';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userType, setUserType] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockoutTime, setLockoutTime] = useState(null);
  const navigate = useNavigate();

  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  // Create rate limiter instance
  const rateLimiter = createRateLimiter(20, 15 * 60 * 1000); // 5 attempts per 15 minutes

  // Check for existing lockout on component mount
  useEffect(() => {
    const savedLockout = localStorage.getItem('loginLockout');
    if (savedLockout) {
      const lockoutEnd = new Date(parseInt(savedLockout));
      if (lockoutEnd > new Date()) {
        setLockoutTime(lockoutEnd);
      } else {
        localStorage.removeItem('loginLockout');
      }
    }
  }, []);

  // Update lockout timer
  useEffect(() => {
    if (lockoutTime) {
      const timer = setInterval(() => {
        if (new Date() >= lockoutTime) {
          setLockoutTime(null);
          localStorage.removeItem('loginLockout');
          setAttempts(0);
          rateLimiter.reset(email);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockoutTime, email]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    // Validate email
    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email address");
      return;
    }

    // Enforce IIT Ropar domain for Internal users
    if (userType === 'Internal' && !email.toLowerCase().endsWith('@iitrpr.ac.in')) {
      setErrorMessage("Only @iitrpr.ac.in email addresses can log in as Internal users.");
      return; // block submit
    }

    // Check if user is locked out
    if (lockoutTime) {
      const remainingTime = Math.ceil((lockoutTime - new Date()) / 1000 / 60);
      setErrorMessage(`Too many attempts. Please try again in ${remainingTime} minutes.`);
      return;
    }

    // Check rate limit
    if (!rateLimiter.check(email)) {
      const lockoutEnd = new Date(Date.now() + 15 * 60 * 1000);
      setLockoutTime(lockoutEnd);
      localStorage.setItem('loginLockout', lockoutEnd.getTime());
      setErrorMessage("Too many attempts. Please try again in 15 minutes.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASED_URL}api/login`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          email: sanitizeInput(email),
          password,
          userType: sanitizeInput(userType)
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Reset attempts on successful login
        setAttempts(0);
        localStorage.removeItem('loginLockout');
        rateLimiter.reset(email);

        // Store auth token
        setAuthToken(data.token);
        localStorage.setItem("ClientUserId", sanitizeInput(email));

        onLogin(data.token);
        navigate("/booking");
      } else {
        // Increment attempts on failed login
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);

        if (newAttempts >= 5) {
          // Set 15-minute lockout
          const lockoutEnd = new Date(Date.now() + 15 * 60 * 1000);
          setLockoutTime(lockoutEnd);
          localStorage.setItem('loginLockout', lockoutEnd.getTime());
          setErrorMessage("Too many failed attempts. Please try again in 15 minutes.");
        } else {
          setErrorMessage(data.message || "Invalid credentials. Please try again.");
        }
      }
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterRedirect = () => {
    navigate("/register");
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotMessage("");

    if (!forgotEmail.trim()) {
      setForgotError("Please enter your email address.");
      return;
    }

    if (!validateEmail(forgotEmail)) {
      setForgotError("Please enter a valid email address.");
      return;
    }

    try {
      setForgotLoading(true);
      const response = await fetch(`${API_BASED_URL}api/forgot-password`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      });

      const data = await response.json();

      if (response.ok) {
        setForgotMessage(data.message || "If this email is registered, you will receive a password reset email shortly.");
        setForgotEmail("");
      } else if (response.status === 429) {
        setForgotError("Too many requests. Please try again after an hour.");
      } else {
        // Show generic message for security (don't reveal if email exists)
        setForgotMessage("If this email is registered and verified, you will receive a password reset email shortly.");
        setForgotEmail("");
      }
    } catch (error) {
      setForgotError("Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPassword(false);
    setForgotEmail("");
    setForgotMessage("");
    setForgotError("");
  };

  return (
    <div className="login-container">
      <h2>Login</h2>
      <form onSubmit={handleLogin}>
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(sanitizeInput(e.target.value))}
            required
            disabled={!!lockoutTime}
            autoComplete="email"
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <div className="relative">
            <input
              type={isPasswordVisible ? "text" : "password"}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={!!lockoutTime}
              autoComplete="current-password"
              className="w-full pr-10"
            />
            <button
              type="button"
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
              onClick={() => setIsPasswordVisible(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex w-7 h-7 items-center justify-center p-0.5 rounded-md text-gray-500 hover:text-gray-700 bg-transparent hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {isPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="userType">User Type:</label>
          <select
            id="userType"
            name="userType"
            value={userType}
            onChange={(e) => setUserType(sanitizeInput(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
            disabled={!!lockoutTime}
          >
            <option value="">Select User Type</option>
            <option value="Internal">Internal</option>
            <option value="Government R&D Lab or External Academics">Government R&D Lab or External Academics</option>
            <option value="Private Industry or Private R&D Lab">Private Industry or Private R&D Lab</option>
          </select>
        </div>
        {errorMessage && <p className="error-message">{errorMessage}</p>}
        {lockoutTime && (
          <p className="error-message">
            Account temporarily locked. Please try again in {Math.ceil((lockoutTime - new Date()) / 1000 / 60)} minutes.
          </p>
        )}
        <button type="submit" disabled={isLoading || !!lockoutTime}>
          {isLoading ? "Logging in..." : "Login"}
        </button>
      </form>

      {/* Forgot Password Link */}
      <button
        type="button"
        className="forgot-password-link"
        onClick={() => setShowForgotPassword(true)}
        style={{
          background: 'none',
          border: 'none',
          color: '#2563eb',
          cursor: 'pointer',
          textDecoration: 'underline',
          fontSize: '14px',
          marginTop: '10px',
          marginBottom: '10px'
        }}
      >
        Forgot Password?
      </button>

      <button
        className="register-button"
        onClick={handleRegisterRedirect}
        disabled={!!lockoutTime}
      >
        Register
      </button>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeForgotPasswordModal}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1f2937' }}>Reset Password</h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              Enter your registered email address. If the email is verified, we'll send you a new temporary password.
            </p>
            <form onSubmit={handleForgotPassword}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label htmlFor="forgotEmail" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Email:</label>
                <input
                  type="email"
                  id="forgotEmail"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="Enter your email"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>

              {forgotError && (
                <p style={{ color: '#dc2626', fontSize: '14px', marginBottom: '15px' }}>{forgotError}</p>
              )}

              {forgotMessage && (
                <p style={{ color: '#16a34a', fontSize: '14px', marginBottom: '15px', backgroundColor: '#f0fdf4', padding: '10px', borderRadius: '6px' }}>{forgotMessage}</p>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={closeForgotPasswordModal}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: forgotLoading ? 'not-allowed' : 'pointer',
                    opacity: forgotLoading ? 0.7 : 1
                  }}
                >
                  {forgotLoading ? "Sending..." : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;

